import { SpecialityMatchTypeToggle } from "@src/common/SpecialityMatchTypeToggle";
import {
  AgeRangeOptions,
  CareFormatOptions,
  CLINICIAN_TYPE,
  FilteredSpecialistOptions,
  LanguagesOptions,
  LegacyFilteredSpecialistOptions,
  PaymentMethodOptions,
  ProviderGenderOptions,
  ServiceTypeOptions,
  SessionTypeOptions,
  THERAPY,
  WeekdayOptions,
} from "@src/constants";
import type { SpecialityMatchType } from "@src/constants/filter-constants";
import {
  type Address,
  type SharedData,
  useApplicationContext,
} from "@src/context/ApplicationContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useFilterContext } from "@src/context/FilterContext";
import { useProviderListContext } from "@src/context/ProviderListContext";
import { useReasonsForVisit } from "@src/hooks/useReasonsForVisit";
import { useInsurancePackages } from "@src/routes/GetStarted/hooks/useInsurancePackages";
import { fetchAddressSearch } from "@src/services";
import ClientLogsService from "@src/services/client-logs.service";
import { getTimezoneData } from "@src/services/get-timezone.service";
import {
  buildProviderSearchRequest,
  getProviderList,
  getProviderSearchSortOverrides,
} from "@src/services/provider-search.service";
import { mapProviderList } from "@src/utils/mapping-utils";
import { isUnder12AgeRange } from "@src/utils/scheduling-metadata";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Accordion } from "@uhg-abyss/web/ui/Accordion";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Checkbox } from "@uhg-abyss/web/ui/Checkbox";
import { CheckboxGroup } from "@uhg-abyss/web/ui/CheckboxGroup";
import { Drawer } from "@uhg-abyss/web/ui/Drawer";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { Layout } from "@uhg-abyss/web/ui/Layout";
import { Link } from "@uhg-abyss/web/ui/Link";
import { RadioGroup } from "@uhg-abyss/web/ui/RadioGroup";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Text } from "@uhg-abyss/web/ui/Text";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { type ChangeEvent, type FC, Fragment, type ReactNode, useEffect, useState } from "react";
import { isPrescriberProviderType } from "../utils/provider-filter-utils";
import { getFilteredReasonForVisitOptions } from "./ReasonForVisitOrdering";

// Service import removed - not currently used in this component

interface AccordionLayoutProps {
  value: string;
  header: string;
  content: ReactNode;
  testId?: string;
}

const DEFAULT_SPECIALITY_MATCH_TYPE: SpecialityMatchType = "or";

const APPOINTMENT_TIME_OPTIONS = Array.from({ length: 10 }, (_, index) => {
  const hour = index + 9;
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return {
    label: `${normalizedHour}:00 ${suffix}`,
    value: hour,
  };
});

const resolveCareFormatForFindProvider = (
  typeOfService?: string,
  providerType?: string,
  careFormat?: string,
): string => {
  const isTherapyService = (typeOfService || THERAPY) === THERAPY;
  const isTherapistProvider =
    (providerType || CLINICIAN_TYPE.THERAPIST) === CLINICIAN_TYPE.THERAPIST;

  if (!isTherapyService || !isTherapistProvider) {
    return "";
  }

  return careFormat || "Individual";
};

const toKebabCase = (value: string): string => {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
};

const isStringValue = (value: string | number): value is string => {
  return typeof value === "string";
};

const isAddress = (value: unknown): value is Address => {
  const address = value as Partial<Address> | undefined;
  return Boolean(
    address?.city &&
      address?.state &&
      address?.state_code &&
      address.position &&
      typeof address.context_id === "number",
  );
};

const getSelectedDays = (selectedValues: Array<string | number> | null | undefined): string[] => {
  return (selectedValues ?? []).filter(isStringValue);
};

const RESET_SHOW_ALL_STATE = {
  specialities: false,
  languages: false,
};

const normalizeAppointmentHour = (
  selectedValue: string | number | null | undefined,
): number | undefined => {
  const numericValue = Number(selectedValue);
  return Number.isFinite(numericValue) && numericValue >= 9 && numericValue <= 18
    ? numericValue
    : undefined;
};

const AccordionLayout: FC<AccordionLayoutProps> = ({ value, header, content, testId }) => {
  return (
    <Accordion.Item
      value={value}
      css={{
        "abyss-icon-symbol": {
          color: "$app.color.icon.primary.interactive !important",
        },
      }}
      data-testid={testId}
    >
      <Accordion.Header
        data-testid={`test-id-secondary-filters-${value}-header`}
        css={{
          "abyss-modal-base-close-button": {
            color: "$app.color.icon.primary.interactive !important",
          },
          "abyss-icon-symbol": {
            color: "$app.color.icon.primary.interactive !important",
          },
        }}
      >
        {header}
      </Accordion.Header>
      <Accordion.Content data-testid={`test-id-secondary-filters-${value}-content`}>
        {content}
      </Accordion.Content>
    </Accordion.Item>
  );
};

const FilterAccordion = ({
  setIsOpen,
  activeFilter,
  onActiveFilterChange,
  isMobile,
  isTablet,
  isMediumMax,
  isWideTabletMax,
  isBrowseIntakePending,
}: {
  setIsOpen: (v: boolean) => void;
  activeFilter?: string;
  onActiveFilterChange: (filter: string | undefined) => void;
  isMobile: boolean;
  isTablet: boolean;
  isMediumMax: boolean;
  isWideTabletMax: boolean;
  isBrowseIntakePending: boolean;
}) => {
  const { data, setData, setDataPatch } = useApplicationContext(); // Access Application context
  const { FEATURE_UPDATED_REASON_FOR_VISIT, FEATURE_REASONS_FOR_VISIT_API } = useFeatureFlags();
  const { reasonsForVisit, error: reasonsForVisitError } = useReasonsForVisit();
  const { fetchProviders, sortBy } = useProviderListContext();
  const ageRangeOptions = AgeRangeOptions;
  const [searchTerm, setSearchTerm] = useState("");
  const [tempZip, setTempZip] = useState(data.zip ?? "");
  const [zipError, setZipError] = useState("");
  const [tempInsurancePolicy, setTempInsurancePolicy] = useState(
    data.insurancePolicy?.id ? String(data.insurancePolicy.id) : "",
  );
  const [tempPaymentMethod, setTempPaymentMethod] = useState(data.paymentMethod ?? "self_pay");
  const {
    secondaryFilter,
    setSecondaryFilter,
    resetSecondaryFilters,
    secondaryFilterInitialState,
    primaryFilterInitialState,
    setPrimaryFilter,
    primaryFilter,
    resetPrimaryFilters,
  } = useFilterContext();
  const [showAll, setShowAll] = useState({
    specialities: false,
    languages: false,
  });

  const [tempSecondaryFilter, setTempSecondaryFilter] = useState(secondaryFilterInitialState);
  const [tempPrimaryFilter, setTempPrimaryFilter] = useState(primaryFilterInitialState);
  const { insurancePolicies } = useInsurancePackages(
    isBrowseIntakePending ? data.userUuid : undefined,
    isBrowseIntakePending && data.address?.context_id ? String(data.address.context_id) : undefined,
    isBrowseIntakePending ? data.address?.state_code : undefined,
    isBrowseIntakePending ? tempPaymentMethod : "self_pay",
  );

  const insurancePolicyOptions = insurancePolicies.map((policy) => ({
    label: policy.name,
    value: String(policy.id),
  }));

  const handleDayOfWeekSelectChange = (
    selectedValues: Array<string | number> | null | undefined,
  ) => {
    setTempSecondaryFilter((prev) => {
      return {
        ...prev,
        dayOfWeek: getSelectedDays(selectedValues),
      };
    });
  };

  useEffect(() => {
    setTempSecondaryFilter(secondaryFilter); // Sync tempSecondaryFilter with secondaryFilter
  }, [secondaryFilter]);

  useEffect(() => {
    setTempPrimaryFilter(primaryFilter); // Sync tempPrimaryFilter with primaryFilters
  }, [primaryFilter]);

  useEffect(() => {
    if (data.isUnder12InPersonProceed) {
      setTempSecondaryFilter((prev) => ({ ...prev, sessionType: "In-person" }));
    }
  }, [data.isUnder12InPersonProceed]);

  // If you want to track checkbox changes individually, uncomment this
  //const handleCheckboxChange = (category: string, value: string, checked: boolean) => {
  //   console.log(`Checkbox change - Category: ${category}, Value: ${value}, Checked: ${checked}`);
  //   setTempSecondaryFilter((prev) => {
  //     const updatedCategory = checked ? [...prev[category], value] : prev[category].filter((item) => item !== value);
  //     return { ...prev, [category]: updatedCategory };
  //   });
  // };

  const renderCheckboxGroup = (
    options,
    category: string,
    showAllKey?: keyof typeof showAll,
    disabledValues: string[] = [],
  ) => {
    const isPrimary = category === "typeOfService" || category === "insurancePolicy";

    let value: string[];
    if (isPrimary) {
      value = tempPrimaryFilter[category] ? [tempPrimaryFilter[category]] : [];
    } else if (Array.isArray(tempSecondaryFilter[category])) {
      value = tempSecondaryFilter[category];
    } else if (
      typeof tempSecondaryFilter[category] === "string" &&
      tempSecondaryFilter[category] !== "no preference" &&
      tempSecondaryFilter[category] !== ""
    ) {
      value = [tempSecondaryFilter[category]];
    } else {
      value = [];
    }

    const displayOptions =
      category === "insurancePolicy" || category === "typeOfService"
        ? value // Only show checked insurance(s)
        : options?.slice(0, showAllKey && showAll[showAllKey] ? options.length : 8) || [];

    const onChange = (selectedValues) => {
      if (isPrimary) {
        // Only allow one selection for primary filters
        setTempPrimaryFilter((prev) => {
          return { ...prev, [category]: selectedValues[0] || "" };
        });
      } else {
        setTempSecondaryFilter((prev) => {
          return {
            ...prev,
            [category]: selectedValues,
          };
        });
      }
    };

    return (
      <CheckboxGroup
        data-testid={`test-id-${toKebabCase(category)}-checkbox-group`}
        label=""
        value={value}
        onChange={onChange}
      >
        <CheckboxGroup.Column>
          {displayOptions.map((option) => {
            return (
              <Checkbox
                key={option}
                label={option}
                value={option}
                css={{
                  "abyss-checkbox-label": { fontWeight: "700 !important" },
                }}
                isDisabled={isPrimary || disabledValues.includes(option)}
                data-testid={`test-id-secondary-filters-${category}-${option}-checkbox-option`}
              />
            );
          })}
        </CheckboxGroup.Column>
      </CheckboxGroup>
    );
  };

  const renderRadioGroup = (options, category: string, showAllKey?: keyof typeof showAll) => {
    const isPrimary = category === "ageRange";
    const value = isPrimary ? tempPrimaryFilter[category] : tempSecondaryFilter[category];
    const displayOptions =
      options?.slice(0, showAllKey && showAll[showAllKey] ? options.length : 8) || [];
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const selectedValue = e.target.value;
      const selectedOption =
        options.find((option) => {
          return option === selectedValue;
        }) || "";
      if (isPrimary) {
        setTempPrimaryFilter((prev) => {
          return {
            ...prev,
            [category]: selectedOption,
          };
        });
      } else {
        setTempSecondaryFilter((prev) => {
          return {
            ...prev,
            [category]: selectedOption,
          };
        });
      }
    };

    return (
      <RadioGroup
        data-testid={`test-id-secondary-filters-${category}-radio-group`}
        label=""
        value={value} // Select the first value if available
        onChange={handleChange}
      >
        <RadioGroup.Column>
          {displayOptions.map((option) => {
            return (
              <RadioGroup.Radio
                key={option}
                label={option}
                value={option}
                css={{ "abyss-radio-label": { fontWeight: "700 !important" } }}
                data-testid={`test-id-secondary-filters-${category}-${option}-radio-option`}
              />
            );
          })}
        </RadioGroup.Column>
      </RadioGroup>
    );
  };

  const renderShowMoreLess = (showAll, setShowAll, optionsLength, category) => {
    const isExpanded = showAll[category];
    if (optionsLength > 8) {
      if (typeof isExpanded !== "boolean") {
        return null;
      }

      return (
        <Link
          data-testid={`test-id-secondary-filters-${category}-show-more-less-link`}
          onClick={() => {
            return setShowAll((prev) => {
              return { ...prev, [category]: !prev[category] };
            });
          }}
          variant="underlined"
        >
          {isExpanded ? "Show less" : `More ${category.toLowerCase()}`}
        </Link>
      );
    }
    return null;
  };

  const renderDayOfWeekSelect = () => {
    return (
      <SelectInput
        id="obc-secondary-filters-day-of-week-select"
        data-testid="test-id-secondary-filters-day-of-week-select"
        aria-label="Days Available"
        label=""
        isMulti
        value={tempSecondaryFilter.dayOfWeek || []}
        options={WeekdayOptions.map((day) => ({ label: day, value: day }))}
        onChange={handleDayOfWeekSelectChange}
      />
    );
  };

  const renderAppointmentTimeSelect = (
    category: "startHour" | "endHour",
    ariaLabel: string,
    inputId: string,
    options: Array<{ label: string; value: number; isDisabled?: boolean }>,
  ) => {
    return (
      <SelectInput
        id={inputId}
        data-testid={`test-id-secondary-filters-${category}-select`}
        aria-label={ariaLabel}
        label={ariaLabel}
        css={{ "abyss-select-input-label": { fontWeight: "bold" } }}
        value={tempSecondaryFilter[category]}
        options={options}
        isClearable
        onChange={(selectedValue: string | number | null | undefined) => {
          const nextValue = normalizeAppointmentHour(selectedValue);

          setTempSecondaryFilter((prev) => {
            if (category === "endHour") {
              return {
                ...prev,
                endHour: nextValue,
              };
            }

            const endHourDelta = Number(nextValue) - Number(prev.endHour);
            const shouldClearEndHour = Number.isFinite(endHourDelta) && endHourDelta > 0;

            return {
              ...prev,
              startHour: nextValue,
              endHour: shouldClearEndHour ? undefined : prev.endHour,
            };
          });
        }}
      />
    );
  };

  const renderSpecialityMatchTypeToggle = () => {
    return (
      <SpecialityMatchTypeToggle
        testIdPrefix="test-id-secondary-filters"
        css={{ marginBottom: "16px" }}
        value={tempSecondaryFilter.specialityMatchType || DEFAULT_SPECIALITY_MATCH_TYPE}
        onChange={(specialityMatchType) => {
          setTempSecondaryFilter((prev) => {
            return {
              ...prev,
              specialityMatchType,
            };
          });
        }}
      />
    );
  };

  const callProviderSearchApi = (filterData: Partial<SharedData>) => {
    return fetchProviders(async () => {
      try {
        // Preserves sort_by/sort_zipcode on Apply/Reset when Distance sort is active.
        const sortOverrides = getProviderSearchSortOverrides(sortBy, filterData);
        const requestedParams = Object.keys(sortOverrides).length
          ? buildProviderSearchRequest(filterData, sortOverrides)
          : buildProviderSearchRequest(filterData);
        const response = await getProviderList(requestedParams);
        if (response?.error) {
          setData("providerList", []);
          ClientLogsService.logError(
            filterData.userUuid || "unknown",
            "FilterAccordion.callProviderSearchApi",
            `Failed to fetch provider list: ${response.error}`,
            new Error(response.error),
          );
          return [];
        }
        const mappedProviders = mapProviderList(response?.message ?? []);
        setData("providerList", mappedProviders);
        return mappedProviders;
      } catch (error) {
        if ((error as { name?: string })?.name === "AbortError") {
          return [];
        }
        setData("providerList", []);
        ClientLogsService.logError(
          filterData.userUuid || "unknown",
          "FilterAccordion.callProviderSearchApi",
          "Error fetching provider list",
          error instanceof Error ? error : new Error("Unknown error"),
        );
        return [];
      }
    });
  };

  const applyFilters = async () => {
    let filterData: Partial<SharedData> = data;
    if (isBrowseIntakePending) {
      let address = data.address;
      let userTimezone = data.userTimezone;
      const normalizedZip = tempZip.trim();

      if (!/^\d{5}$/.test(normalizedZip)) {
        setZipError("Enter a valid 5-digit ZIP code.");
        return;
      }

      if (normalizedZip !== data.zip) {
        const addressResult = await fetchAddressSearch(
          normalizedZip,
          data.userUuid,
          data.userRole,
          data.testRecord,
        );
        if (!isAddress(addressResult)) {
          setZipError(
            (addressResult as { error?: string } | undefined)?.error ||
              "Enter a valid 5-digit ZIP code.",
          );
          return;
        }

        const timezoneResult = await getTimezoneData(
          addressResult.position.lat,
          addressResult.position.lon,
          data.userUuid || "unknown",
        );
        if (!timezoneResult?.success || !timezoneResult.data) {
          setZipError("We could not determine the timezone for that ZIP code.");
          return;
        }

        address = addressResult;
        userTimezone = timezoneResult.data;
      }

      const insurancePolicy =
        tempPaymentMethod === "insurance"
          ? insurancePolicies.find((policy) => String(policy.id) === tempInsurancePolicy)
          : undefined;
      const combinedFilterPatch: Partial<SharedData> = {
        address,
        zip: normalizedZip,
        userTimezone,
        typeOfService: tempPrimaryFilter.typeOfService,
        ageRange: tempPrimaryFilter.ageRange,
        careFormat: tempSecondaryFilter.careFormat,
        paymentMethod: tempPaymentMethod,
        insurancePolicy: insurancePolicy ?? null,
        selectedPolicy: insurancePolicy ? String(insurancePolicy.id) : null,
      };
      setZipError("");
      setDataPatch(combinedFilterPatch);
      filterData = { ...data, ...combinedFilterPatch };
    }

    setSecondaryFilter(tempSecondaryFilter);
    // Merge primary + secondary temporary filters into a single view of applied filters
    const mergedFilters = { ...tempPrimaryFilter, ...tempSecondaryFilter };
    const resolvedCareFormat = resolveCareFormatForFindProvider(
      mergedFilters.typeOfService,
      mergedFilters.providerType,
      mergedFilters.careFormat,
    );
    mergedFilters.careFormat = resolvedCareFormat;

    // Ensure primaryFilter is updated for keys that are treated as primary (keep UI in sync)
    setPrimaryFilter((prev) => {
      return {
        ...prev,
        ageRange: mergedFilters.ageRange,
        careFormat: resolvedCareFormat,
      };
    });
    // Session type selections are preserved as-is so in-person/virtual choices persist
    // regardless of the scheduling consolidation flag.
    ["sessionType", "providerGender"].forEach((key) => {
      if (!Array.isArray(mergedFilters[key])) {
        return;
      }

      const selectedValues = mergedFilters[key];

      if (key === "sessionType") {
        mergedFilters[key] = selectedValues.length > 0 ? selectedValues : "no preference";
        return;
      }

      if (selectedValues.length > 1) {
        mergedFilters[key] = "no preference";
        return;
      }

      mergedFilters[key] = selectedValues[0] || "no preference";
    });

    Object.entries(mergedFilters).forEach(([key, value]) => {
      key !== "insurancePolicy" && setData(key, value);
    });

    // Sync isUnder12InPersonProceed with the applied age range
    if (isUnder12AgeRange(mergedFilters.ageRange)) {
      setData("isUnder12InPersonProceed", true);
      setData("sessionType", "In-person");
      mergedFilters.sessionType = "In-person";
    } else if (data.isUnder12InPersonProceed) {
      // If the user switched to a non-under-12 age range, lift the in-person lock
      setData("isUnder12InPersonProceed", false);
    }

    setIsOpen(false);

    await callProviderSearchApi({
      ...filterData,
      ageRange: mergedFilters.ageRange,
      careFormat: resolvedCareFormat,
      dayOfWeek: mergedFilters.dayOfWeek,
      startHour: mergedFilters.startHour,
      endHour: mergedFilters.endHour,
      languages: mergedFilters.languages,
      providerGender: mergedFilters.providerGender,
      providerRadius: mergedFilters.providerRadius,
      providerType: mergedFilters.providerType,
      sessionType: mergedFilters.sessionType,
      specialities: mergedFilters.specialities,
      specialityMatchType: mergedFilters.specialityMatchType,
      insurancePolicy: isBrowseIntakePending ? filterData.insurancePolicy : data.insurancePolicy,
    } as Partial<SharedData>);
  };

  const resetFilters = async () => {
    setSearchTerm("");
    resetSecondaryFilters(); // Reset the context filters
    resetPrimaryFilters(); // Reset primary filters
    setTempSecondaryFilter(secondaryFilterInitialState);
    //reset data in Application context
    setData("specialities", []);
    setData("specialityMatchType", DEFAULT_SPECIALITY_MATCH_TYPE);
    // setData("modalities", []);
    setData("languages", "");
    setData("providerGender", "");
    setData("dayOfWeek", []);
    setData("startHour", undefined);
    setData("endHour", undefined);
    // Preserve In-person lock when under-12 flag is active
    if (!data.isUnder12InPersonProceed) {
      setData("sessionType", "no preference");
    }
    setData("providerRadius", "");
    if (isBrowseIntakePending) {
      setTempZip(data.zip ?? "");
      setZipError("");
      setTempPaymentMethod(data.paymentMethod ?? "self_pay");
      setTempInsurancePolicy(data.insurancePolicy?.id ? String(data.insurancePolicy.id) : "");
    }
    setIsOpen(false);

    await callProviderSearchApi({
      ...data,
      dayOfWeek: [],
      startHour: undefined,
      endHour: undefined,
      languages: "",
      providerGender: "",
      providerRadius: "",
      sessionType: data.isUnder12InPersonProceed ? data.sessionType : "no preference",
      specialities: [],
      specialityMatchType: DEFAULT_SPECIALITY_MATCH_TYPE,
    } as Partial<SharedData>);

    setShowAll(RESET_SHOW_ALL_STATE);
  };

  const handleSubmit = async (e) => {
    const buttonId = e.nativeEvent.submitter?.id;
    e.preventDefault();
    if (buttonId === "obc-secondary-filters-apply-button") {
      await applyFilters();
    } else if (buttonId === "obc-secondary-filters-reset-button") {
      await resetFilters();
    }
  };

  return (
    <form
      id="secondary-filters-form"
      data-testid="test-id-secondary-filters-form"
      onSubmit={(e) => {
        return handleSubmit(e);
      }}
    >
      <Accordion
        data-testid="test-id-secondary-filters-accordion"
        isCollapsible
        value={activeFilter}
        onValueChange={(e) => {
          onActiveFilterChange(e || undefined);
          // Reset search term when certain accordions are opened
          if (e === "languages" || e === "specialities") {
            setSearchTerm("");
          }
        }}
      >
        {isBrowseIntakePending && (
          <Fragment>
            <AccordionLayout
              value="zip-code"
              header="Zip code"
              content={
                <TextInput
                  label="Patient zip code"
                  autoComplete="postal-code"
                  value={tempZip}
                  onChange={(event) => {
                    setZipError("");
                    setTempZip(event.target.value);
                  }}
                  errorMessage={zipError}
                />
              }
            />
            <AccordionLayout
              value="type-of-care"
              header="Type of care"
              content={
                <SelectInput
                  label="Type of care"
                  value={tempPrimaryFilter.typeOfService}
                  options={ServiceTypeOptions.map((serviceType) => ({
                    value: serviceType,
                    label: serviceType,
                  }))}
                  onChange={(selectedValue) => {
                    setTempPrimaryFilter((previous) => ({
                      ...previous,
                      typeOfService: String(selectedValue ?? ""),
                    }));
                  }}
                />
              }
            />
            <AccordionLayout
              value="age-range"
              header="Age range served"
              content={
                <SelectInput
                  label="Age range served"
                  value={tempPrimaryFilter.ageRange}
                  options={ageRangeOptions.map((ageRange) => ({
                    value: ageRange,
                    label: ageRange,
                  }))}
                  onChange={(selectedValue) => {
                    setTempPrimaryFilter((previous) => ({
                      ...previous,
                      ageRange: String(selectedValue ?? ""),
                    }));
                  }}
                />
              }
            />
            <AccordionLayout
              value="service-type"
              header="Service type"
              content={
                <SelectInput
                  label="Service type"
                  value={tempSecondaryFilter.careFormat}
                  options={CareFormatOptions.map((careFormat) => ({
                    value: careFormat,
                    label: careFormat,
                  }))}
                  onChange={(selectedValue) => {
                    setTempSecondaryFilter((previous) => ({
                      ...previous,
                      careFormat: String(selectedValue ?? ""),
                    }));
                  }}
                />
              }
            />
            <AccordionLayout
              value="payment-method"
              header="Payment method"
              content={
                <SelectInput
                  label="Payment method"
                  value={tempPaymentMethod}
                  options={PaymentMethodOptions}
                  onChange={(selectedValue) => {
                    setTempInsurancePolicy("");
                    setTempPaymentMethod(String(selectedValue ?? "self_pay"));
                  }}
                />
              }
            />
            {tempPaymentMethod === "insurance" && (
              <AccordionLayout
                value="insurance"
                header="Insurance"
                content={
                  <SelectInput
                    label="Insurance"
                    value={tempInsurancePolicy}
                    options={insurancePolicyOptions}
                    isDisabled={insurancePolicyOptions.length === 0}
                    isSearchable
                    onChange={(selectedValue) =>
                      setTempInsurancePolicy(String(selectedValue ?? ""))
                    }
                  />
                }
              />
            )}
          </Fragment>
        )}
        {(isMobile || isTablet) && !isBrowseIntakePending && (
          <AccordionLayout
            value="age-range"
            header="Age range served"
            content={renderRadioGroup(ageRangeOptions, "ageRange")}
          />
        )}

        <AccordionLayout
          value="gender"
          header="Provider gender"
          content={renderCheckboxGroup(ProviderGenderOptions, "providerGender")}
        />

        <AccordionLayout
          value="session-type"
          header="Session type"
          testId="test-id-session-type-filter-accordion"
          content={renderCheckboxGroup(
            SessionTypeOptions,
            "sessionType",
            undefined,
            data.isUnder12InPersonProceed ? ["In-person", "Virtual"] : [],
          )}
        />

        {(isMobile || isWideTabletMax) && !isPrescriberProviderType(tempSecondaryFilter) && (
          <AccordionLayout
            value="care-format"
            header="Service type"
            content={renderRadioGroup(CareFormatOptions, "careFormat")}
          />
        )}

        <AccordionLayout
          value="days-available"
          header="Days available"
          content={renderDayOfWeekSelect()}
        />

        <AccordionLayout
          value="appointment-time"
          header="Appointment time"
          content={
            <Layout.Stack css={{ gap: "16px" }}>
              {renderAppointmentTimeSelect(
                "startHour",
                "Earliest Start Time",
                "obc-secondary-filters-start-hour-select",
                APPOINTMENT_TIME_OPTIONS,
              )}
              {renderAppointmentTimeSelect(
                "endHour",
                "Latest Start Time",
                "obc-secondary-filters-end-hour-select",
                APPOINTMENT_TIME_OPTIONS.map((option) => {
                  return {
                    ...option,
                    isDisabled:
                      typeof tempSecondaryFilter.startHour === "number"
                        ? option.value < tempSecondaryFilter.startHour
                        : false,
                  };
                }),
              )}
            </Layout.Stack>
          }
        />

        <AccordionLayout
          value="specialities"
          header="Reason for visit"
          content={
            <div>
              {renderSpecialityMatchTypeToggle()}
              <TextInput
                data-testid="test-id-secondary-filters-specialities-search"
                value={searchTerm}
                onChange={(e) => {
                  return setSearchTerm(e.target.value.toLowerCase());
                }}
                inputLeftElement={{
                  element: (
                    <IconSymbol
                      icon="search"
                      size="$web.semantic.sizing.icon.utility.sm"
                      color="$input-v2.color.icon.content"
                    />
                  ),
                  description: "Search Specialities",
                }}
              />
              {reasonsForVisitError && (
                <Text
                  data-testid="test-id-secondary-filters-specialities-error"
                  css={{ color: "$app.color.text.status.error" }}
                  role="alert"
                >
                  {reasonsForVisitError}
                </Text>
              )}
              {(() => {
                const legacySpecialistOptions = FEATURE_UPDATED_REASON_FOR_VISIT
                  ? FilteredSpecialistOptions
                  : LegacyFilteredSpecialistOptions;
                const specialistOptions = FEATURE_REASONS_FOR_VISIT_API
                  ? reasonsForVisit.map((reason) => reason.display)
                  : legacySpecialistOptions;
                const filteredOptions = getFilteredReasonForVisitOptions(
                  specialistOptions,
                  searchTerm,
                  tempSecondaryFilter.specialities,
                );
                const length = filteredOptions.length;
                return (
                  <Fragment>
                    {renderCheckboxGroup(filteredOptions, "specialities", "specialities")}
                    {renderShowMoreLess(showAll, setShowAll, length, "specialities")}
                  </Fragment>
                );
              })()}
            </div>
          }
        />

        {/* <AccordionLayout
          value="modalities"
          header="Therapy modality"
          content={
            < div >
              {renderCheckboxGroup(ModalitiesOptions, "modalities", "modalities")}
              {renderShowMoreLess(showAll.modalities, setShowAll, ModalitiesOptions.length, 'modalities')}
            </div >
          }
        /> */}

        <AccordionLayout
          value="languages"
          header="Languages"
          content={
            <div>
              <TextInput
                data-testid="test-id-secondary-filters-languages-search"
                onChange={(e) => {
                  return setSearchTerm(e.target.value.toLowerCase());
                }}
                inputLeftElement={{
                  element: (
                    <IconSymbol
                      icon="search"
                      size="$web.semantic.sizing.icon.utility.sm"
                      color="$input-v2.color.icon.content"
                    />
                  ),
                  description: "Search Languages",
                }}
              />
              {(() => {
                const filteredOptions = LanguagesOptions.filter((option) => {
                  return option.toLowerCase().includes(searchTerm);
                });
                const length = filteredOptions.length;
                return (
                  <Fragment>
                    {renderRadioGroup(filteredOptions, "languages", "languages")}
                    {renderShowMoreLess(showAll, setShowAll, length, "languages")}
                  </Fragment>
                );
              })()}
            </div>
          }
        />
        <AccordionLayout
          value="provider-type"
          header="Type of provider"
          content={
            <Checkbox
              label={secondaryFilter.providerType || CLINICIAN_TYPE.THERAPIST}
              isChecked
              isDisabled
              data-testid="test-id-secondary-filters-provider-type-checkbox"
              css={{
                "abyss-checkbox-label": {
                  fontWeight: "700 !important",
                },
              }}
            />
          }
        />

        {isMediumMax && (
          <AccordionLayout
            value="insurance-policy"
            header="Insurance"
            content={renderCheckboxGroup(data.insurancePolicies, "insurancePolicy")}
          />
        )}
        {isMobile && (
          <AccordionLayout
            value="type-of-care"
            header="Type of care"
            content={renderCheckboxGroup(primaryFilter?.typeOfService || THERAPY, "typeOfService")}
          />
        )}
      </Accordion>
    </form>
  );
};

const StyledFiltersContainer = styled("div", {
  whiteSpace: "nowrap",
  display: "flex",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
});

const StyledV2Drawer = styled(Drawer, {
  ".abyss-modal-base-header-container": {
    flexDirection: "column",
    border: "none !important",
  },
  ".abyss-modal-base-close-button": {
    alignSelf: "flex-end",
    ".abyss-icon-symbol": {
      color: "$app.color.icon.primary.interactive !important",
    },
  },
  ".abyss-modal-base-header": {
    fontSize: "26px !important",
    fontWeight: "700",
  },
  ".abyss-accordion-item": {
    borderTop: "1px solid $app.color.default.border !important",
  },
  ".abyss-accordion-header": {
    ".abyss-icon-symbol": {
      color: "$app.color.icon.primary.interactive !important",
    },
    ".abyss-accordion-header-trigger": {
      borderBottom: "none !important",
    },
  },
  ".abyss-modal-base-body": {
    padding: "16px !important",
  },
  ".abyss-modal-base-footer": {
    boxShadow: "none !important",
    ".abyss-layout-group": {
      justifyContent: "space-between !important",
      width: "100%",
      div: {
        width: "100%",
        button: {
          width: "100%",
        },
      },
    },
  },
  variants: {
    isMobile: {
      true: {
        ".abyss-modal-base-header": {
          fontSize: "28px !important",
        },
      },
    },
  },
});

export const SecondaryFilters = ({
  isMobile,
  isTablet,
  isMediumMax,
  isWideTabletMax,
  isBrowseIntakePending = false,
  isOpen,
  onOpenChange,
  activeFilter,
  onActiveFilterChange,
}: {
  isMobile: boolean;
  isTablet: boolean;
  isMediumMax: boolean;
  isWideTabletMax: boolean;
  isBrowseIntakePending?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  activeFilter?: string;
  onActiveFilterChange?: (filter: string | undefined) => void;
}) => {
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const [localActiveFilter, setLocalActiveFilter] = useState<string | undefined>();
  const isDrawerOpen = isOpen ?? localIsOpen;
  const selectedFilter = activeFilter ?? localActiveFilter;
  const updateDrawerOpen = onOpenChange ?? setLocalIsOpen;
  const updateActiveFilter = onActiveFilterChange ?? setLocalActiveFilter;
  const { data } = useApplicationContext();
  const { loading } = useProviderListContext();

  const footer = (
    <Layout.Group>
      <Button
        data-testid="test-id-secondary-filters-reset-button"
        variant="outline"
        form="secondary-filters-form"
        type="submit"
        id="obc-secondary-filters-reset-button"
        data-obc-label="Secondary Filters Reset Button"
        isDisabled={loading}
      >
        Reset
      </Button>
      <Button
        data-testid="test-id-secondary-filters-apply-button"
        variant="filled"
        form="secondary-filters-form"
        type="submit"
        id="obc-secondary-filters-apply-button"
        data-obc-label="Secondary Filters Apply Button"
        isDisabled={loading}
      >
        Apply
      </Button>
    </Layout.Group>
  );

  return (
    <StyledFiltersContainer data-testid="test-id-secondary-filters">
      <Button
        data-testid="test-id-secondary-filters-more-filters-button"
        size={isMobile ? "md" : "lg"}
        variant="outline"
        icon={{ icon: "filter_list", position: "leading", variant: "outlined" }}
        aria-haspopup="dialog"
        onClick={() => {
          return updateDrawerOpen(true);
        }}
        id="obc-secondary-filters-more-filters-button"
        data-obc-label={`Find a provider ${isBrowseIntakePending || isMediumMax ? "Filters" : "More filters"} Button`}
        // Treatment header controls stay interactive while the first provider
        // and availability requests are in flight, matching the POC pills.
        isDisabled={isBrowseIntakePending ? false : loading}
      >
        {isBrowseIntakePending || isMediumMax ? "Filters" : "More filters"}
      </Button>

      <StyledV2Drawer
        data-testid="test-id-secondary-filters-drawer"
        isMobile={isMobile}
        header={isBrowseIntakePending || isMediumMax ? "Filters" : "More filters"}
        model="checkbox-form"
        onClose={() => {
          ClientLogsService.logWebAction(
            data.userUuid || "unknown",
            "SecondaryFilters.onClose",
            "User closed the filters drawer without applying changes.",
          );
          return updateDrawerOpen(false);
        }}
        footer={footer}
        size={isMobile ? "100%" : "375px"}
        isOpen={isDrawerOpen}
      >
        <Text
          data-testid="test-id-secondary-filters-drawer-text"
          css={{
            fontSize: "14px !important",
            fontWeight: "700 !important",
          }}
        >
          Selecting filters and apply will update results
        </Text>
        <FilterAccordion
          setIsOpen={updateDrawerOpen}
          activeFilter={selectedFilter}
          onActiveFilterChange={updateActiveFilter}
          isMobile={isMobile}
          isTablet={isTablet}
          isMediumMax={isMediumMax}
          isWideTabletMax={isWideTabletMax}
          isBrowseIntakePending={isBrowseIntakePending}
        />
      </StyledV2Drawer>
    </StyledFiltersContainer>
  );
};
